import * as React from 'react';
// Import hooks.
import {
	useInputForm,
	UseInputFormResult,
} from '@app/hooks/view/input';
// Import app models.
import {
	ValidatorResponse,
} from '@app/models/app';
// Import props.
import {
	InputFormProps,
	InputNumberOfStoriesSelectionListProps,
} from '@app/props';

interface UseInputNumberOfStoriesSelectionListParameters {
	id: string;
	label: string;
	required: boolean;
	disabled: boolean;
	value?: number;
	showValidation?: boolean;
}

type UseInputNumberOfStoriesSelectionListResults = UseInputFormResult<number | undefined, InputNumberOfStoriesSelectionListProps>

const useInputNumberOfStoriesSelectionList = (params: UseInputNumberOfStoriesSelectionListParameters): UseInputNumberOfStoriesSelectionListResults => {

	// #region exported methods

	/**
	 * Focuses to element. 
	 **/
	const focusToOtherInput = (): void => {
		if (otherInputRef !== undefined
			&& otherInputRef.current !== null) {
			setQueuedInputToFocus(true);
		}
		else {
			focusToInput();
		}
	};

	/**
	 * Returns props for form input. 
	 **/
	const applyInputNumberOfStoriesSelectionListProps = (): InputNumberOfStoriesSelectionListProps => {
		// First apply the form input props.
		let formProps: InputFormProps = applyProps();
		// Then apply the input props.
		return {
			...formProps,
			numberOfStories: other.value,
			onChangeNumberOfStories: other.setInput,
			otherInputRef: registerOtherInputRef,
		}
	};

	// #endregion exported methods


	// #region private methods

	const registerOtherInputRef = (ref: React.RefObject<any>): void => {
		if (otherInputRef === undefined
			|| otherInputRef.current === null) {
			setOtherInputRef(ref);
		}
	}

	/**
	 * Validates the selected value.
	 * @param selectedValue
	 */
	const validateDataSelection = (selectedValue: number | undefined, required: boolean): ValidatorResponse => {
		let response: ValidatorResponse = new ValidatorResponse(true, String.empty());

		if (required
			&& selectedValue === undefined) {
			// Make sure the number of stories is not empty.
			response = new ValidatorResponse(false, 'The number of stories above grade is required.');
		}
		else if (selectedValue !== undefined
			&& selectedValue <= 0) {
			// Make sure the number of stories is valid.
			response = new ValidatorResponse(false, 'The number of stories above grade should be greater than zero.');
		}

		return response;
	}

	// #endregion private methods


	// #region variables

	const [otherInputRef, setOtherInputRef] = React.useState<React.RefObject<any> | undefined>(undefined);
	const [queuedInputToFocus, setQueuedInputToFocus] = React.useState<boolean>(false);

	// Instantiate the 'data selection list' input.
	const { applyProps, focusToInput, ...other } = useInputForm<number | undefined>({
		id: params.id,
		label: params.label,
		required: params.required,
		disabled: params.disabled,
		initialInput: params.value,
		showValidation: params.showValidation,
		validators: [validateDataSelection],
	});

	// #endregion variables

	// #region life-cycle methods.

	// Called when input ref or when the focus-to flag is updated.
	React.useEffect(() => {
		// If there is a queued input focus-to flag and the input reference is ready, then proceed to focus on the input.
		if (queuedInputToFocus
			&& otherInputRef !== undefined
			&& otherInputRef.current !== null) {
			// If input is already focused, then do not attempt to do it again.
			if (document.activeElement !== otherInputRef.current) {
				otherInputRef.current.focus();
			}
			// Reset the queued input to focus.
			setQueuedInputToFocus(false);
		}
	}, [queuedInputToFocus, otherInputRef]);

	// #endregion life-cycle methods.

	return {
		focusToInput: focusToOtherInput,
		applyProps: applyInputNumberOfStoriesSelectionListProps,
		...other,
	};
}

// Export parameters.
export {
	UseInputNumberOfStoriesSelectionListParameters,
}

// Export results.
export {
	UseInputNumberOfStoriesSelectionListResults,
}

export default useInputNumberOfStoriesSelectionList;